import { memo, useCallback, useEffect, useState } from 'react';

import { Button, Icons, SpinnerOverlay, TextArea } from '@components/common';

import { useModal } from '@components/common/Modal/utils';

import { IValueLabel } from '@interfaces/common/common';

import { EButtonType } from '@enums/common';

import { MAX_500_LENGTH } from '@constants/validationMessages';

import styles from './NotesModal.module.scss';

interface INotesModalProps {
	notes: string;
	name: string;
	headerFields?: IValueLabel[];
	isLoading: boolean;
	isEdit?: boolean;
	onSubmit?: (note: string) => Promise<void>;
	setIsDirtyForm: (isDirtyForm: boolean) => void;
	isReadOnly?: boolean;
}

const NotesModal = ({
	isLoading,
	notes,
	name = '',
	headerFields = [],
	setIsDirtyForm,
	isEdit = false,
	onSubmit,
	isReadOnly,
}: INotesModalProps) => {
	const { onClose, setTitleValue } = useModal();

	const [noteText, setNoteText] = useState<string>(notes);
	const [defaultNoteText, setDefaultNoteText] = useState<string>(notes);
	const [isEditNote, setIsEditNote] = useState<boolean>(isEdit);

	const isSubmitDisabled = noteText?.length === 0 || noteText === defaultNoteText;

	const toggleEditMode = useCallback(() => {
		setIsEditNote((isEdit) => {
			setTitleValue(!isEdit ? 'Edit note' : 'View note');

			return !isEdit;
		});
	}, [setTitleValue]);

	const onChange = useCallback((value: string) => {
		setNoteText(value);
		setIsDirtyForm(true);
	}, [setIsDirtyForm]);

	const handleSubmit = useCallback(async () => {
		if (onSubmit) {
			await onSubmit(noteText);

			setDefaultNoteText(noteText);
			toggleEditMode();
			setIsDirtyForm(false);
		}
	}, [
		noteText,
		onSubmit,
		toggleEditMode,
		setIsDirtyForm,
	]);

	const cancelChanges = useCallback(() => {
		setNoteText(defaultNoteText);
		toggleEditMode();
		setIsDirtyForm(false);
	}, [defaultNoteText, toggleEditMode, setIsDirtyForm]);

	useEffect(() => {
		setTitleValue(isEditNote ? 'Edit note' : 'View note');
	// eslint-disable-next-line
	}, []);

	return (
		<div className={styles.wrapper}>
			{isLoading && <SpinnerOverlay />}
			<div className={styles.header}>
				<div className={styles.title}>{name}</div>
				{headerFields.map(({ label, value }) => (
					<div
						key={value}
						className={styles.field}
					>
						{label} <span>{value}</span>
					</div>
				))}
			</div>
			<TextArea
				isMaxLengthCounter={isEditNote}
				isNotResizable
				isWithoutFocus={!isEditNote}
				maxLength={isEditNote ? MAX_500_LENGTH : undefined}
				name='notes'
				onChange={onChange}
				readonly={!isEditNote}
				textAreaClassName={styles.textArea}
				value={noteText}
				valueLength={noteText?.length}
			/>
			<div className={styles.buttonsWrapper}>
				<div className={styles.editButtonWrapper}>
					{!isEditNote && !isReadOnly && (
						<Button
							icon={Icons.editIcon()}
							onClick={toggleEditMode}
							text='Edit'
							withoutBackground
						/>
					)}
				</div>
				<div className={styles.submissionButtonsWrapper}>
					{!isEditNote && (
						<Button
							large
							onClick={onClose}
							text='Close'
						/>
					)}
					{isEditNote && (
						<>
							<Button
								large
								onClick={cancelChanges}
								text='Cancel'
								withoutBackground
							/>
							<Button
								disabled={isSubmitDisabled}
								large
								onClick={handleSubmit}
								text='Save'
								type={EButtonType.submit}
							/>
						</>
					)}
				</div>
			</div>
		</div>
	);
};

export default memo(NotesModal);
